import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from 'react';
import { 
  Box, Button, IconButton, Tooltip, Drawer, Typography, Divider, 
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Slider, Switch, FormControlLabel, Chip, Badge, Fab, Menu, MenuItem,
  Paper, Card, CardContent, Popover, ClickAwayListener, Tabs, Tab,
  Accordion, AccordionSummary, AccordionDetails, Avatar, AvatarGroup,
  List, ListItem, ListItemText, ListItemIcon, Skeleton
} from '@mui/material';
import { 
  AddPhotoAlternate, TextFields, Palette, Layers, Lock, LockOpen,
  CropSquare, RadioButtonUnchecked, EmojiEmotions, Delete,
  FileCopy, CloudDownload, Search, Close,
  FormatBold, FormatItalic, FormatUnderlined, FormatAlignLeft, 
  FormatAlignCenter, FormatAlignRight, ArrowDropDown, Add, Remove,
  Menu as MenuIcon, MoreVert, Fullscreen, FullscreenExit, GridOn,
  ZoomIn, ZoomOut, RestartAlt, Save, People,
  Brush, Gradient, AutoAwesome, Visibility, VisibilityOff,
  DragIndicator, ExpandMore, PhotoLibrary, Category, Timeline,
  TouchApp, Mouse, Smartphone, Tablet, DesktopMac, KeyboardArrowUp,
  KeyboardArrowDown, VerticalAlignTop, VerticalAlignBottom
} from '@mui/icons-material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Stage, Layer, Rect, Text as KonvaText, Image as KonvaImage, Transformer, Ellipse, Line, Arrow } from 'react-konva';
import { collection, doc, onSnapshot, addDoc, updateDoc, deleteDoc, getDoc, setDoc } from 'firebase/firestore';
import { firestore, database } from '../config/firebase';
import { ref, onValue, set, onDisconnect, serverTimestamp as rtdbServerTimestamp } from 'firebase/database';
import { useParams, useNavigate } from 'react-router-dom';
import { SketchPicker } from 'react-color';
import { AuthContext } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions as fbFunctions } from '../config/firebase';

// Enhanced constants for professional layout
const SIDEBAR_WIDTH = 320;
const COMPACT_SIDEBAR_WIDTH = 72;
const BOARD_MIN_WIDTH = 1200;
const BOARD_MIN_HEIGHT = 800;
const MOBILE_BREAKPOINT = 768;

interface BoardElement {
  id: string;
  type: 'image' | 'text' | 'rect' | 'sticker' | 'circle' | 'line' | 'arrow' | 'colorSwatch' | 'brush';
  content: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color?: string;
  fontSize?: number;
  rotation?: number;
  fontFamily?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  align?: string;
  backgroundColor?: string;
  opacity?: number;
  locked?: boolean;
  visible?: boolean;
  layer?: number;
  strokeWidth?: number;
  points?: number[];
  zIndex?: number; // For layer ordering
  timestamp?: number; // For undo/redo
}

interface LayerInfo {
  id: string;
  name: string;
  visible: boolean;
  locked: boolean;
  elements: string[];
}

interface HistoryState {
  elements: BoardElement[];
  timestamp: number;
}

interface TeamMember {
  email: string;
  role: string;
}

interface PresenceData {
  uid: string;
  displayName: string;
  photoURL: string;
  board: string;
  x?: number;
  y?: number;
  lastActive: number;
}

interface OnlineCollaborator {
  id: string;
  name: string;
  avatar: string;
  email: string;
  role: string;
  color: string;
  cursor?: { x: number; y: number };
  lastActive: number;
}

const MoodBoardEditorV3: React.FC<{ boardId: string }> = ({ boardId }) => {
  // UI Theme – harmonize with overall application look (Tailwind blues / rounded corners)
  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          primary: { main: '#3b82f6' },
          secondary: { main: '#6366f1' },
          background: {
            default: '#f5f5f5',
            paper: '#ffffff',
          },
        },
        typography: {
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
          button: { textTransform: 'none', fontWeight: 600 },
        },
        shape: { borderRadius: 8 },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: '0.5rem', // match Tailwind rounded-lg
              },
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                border: '1px solid #e5e7eb',
                backgroundColor: '#ffffff',
                borderRadius: '0.5rem', // square-ish shape instead of circle
                padding: '6px', // uniform padding
                '&:hover': {
                  backgroundColor: '#f1f5f9', // gray-100
                },
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                borderRight: '1px solid #e5e7eb',
              },
            },
          },
        },
      }),
    []
  );
  // Theme and UI State
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [sidebarCompact, setSidebarCompact] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0: Tools, 1: Layers, 2: Assets

  // Canvas State
  const [elements, setElements] = useState<BoardElement[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [layers, setLayers] = useState<LayerInfo[]>([
    { id: 'layer-main', name: 'Main', visible: true, locked: false, elements: [] }
  ]);
  const [activeLayer, setActiveLayer] = useState('layer-main');
  
  // History for Undo/Redo
  const [history, setHistory] = useState<HistoryState[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const prevElementsLenRef = useRef<number>(0);
  
  // Tools State
  const [activeTool, setActiveTool] = useState<string>('select');
  const [color, setColor] = useState('#3b82f6');
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [opacity, setOpacity] = useState(1);
  
  // Canvas Controls
  const [zoom, setZoom] = useState(100);
  const [showGrid, setShowGrid] = useState(false);
  const [snapToGrid, setSnapToGrid] = useState(false);
  const [canvasBackground, setCanvasBackground] = useState('#ffffff');
  
  // Dialogs and Menus
  const [textDialog, setTextDialog] = useState(false);
  const [unsplashDialog, setUnsplashDialog] = useState(false);
  const [aiDialog, setAiDialog] = useState(false);
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [colorPickerAnchor, setColorPickerAnchor] = useState<null | HTMLElement>(null);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Form States
  const [newText, setNewText] = useState('');
  const [unsplashQuery, setUnsplashQuery] = useState('');
  const [unsplashResults, setUnsplashResults] = useState<any[]>([]);
  const [unsplashLoading, setUnsplashLoading] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  
  // Real Collaboration State
  const [team, setTeam] = useState<{ members: Record<string, TeamMember> } | null>(null);
  const [onlineCollaborators, setOnlineCollaborators] = useState<OnlineCollaborator[]>([]);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'error'>('saved');
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [lastModified, setLastModified] = useState<number>(Date.now());

  // Refs
  const stageRef = useRef<any>(null);
  const transformerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Ref to detect first property effect call after changing selection
  const prevSelectedIdRef = useRef<string | null>(null);

  // Navigation & Auth
  const navigate = useNavigate();
  const { teamId } = useParams<{ teamId?: string }>();
  const [teamName, setTeamName] = useState<string>('');
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;

  // New: path prefix for Firestore based on teamId
  const elementsPath = useMemo(
    () =>
      teamId
        ? `teams/${teamId}/moodboards-v3/${boardId}/elements`
        : `moodboards-v3/${boardId}/elements`,
    [teamId, boardId]
  );

  const UNSPLASH_ACCESS_KEY = '2lRBV8sO9SynWMDFMAxuiLE0MiHt6x8hzTpaUc4SveU';
  const boardTag = `moodboard-v3:${boardId}`; // Board identifier for presence, unique per board

  // Predefined colors for collaborators
  const collaboratorColors = [
    '#ef4444', '#22c55e', '#3b82f6', '#f59e0b', '#8b5cf6', 
    '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'
  ];

  // Helper function to format time ago
  const formatTimeAgo = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  // Real-time team data fetching
  useEffect(() => {
    if (!teamId) return;
    
    const teamRef = ref(database, `teams/${teamId}`);
    const unsubTeam = onValue(teamRef, (snapshot) => {
      const teamData = snapshot.val();
      if (teamData) {
        setTeam(teamData);
        setTeamName(teamData.name || 'Team');
        console.log('Team data loaded:', teamData); // Debug log
      }
    });

    return () => unsubTeam();
  }, [teamId]);

  // Real-time presence tracking with mouse cursors
  useEffect(() => {
    if (!teamId || !currentUser || !team) {
      console.log('Presence setup conditions not met:', { teamId, currentUser: !!currentUser, team: !!team });
      return;
    }

    console.log('Setting up presence tracking for user:', currentUser.uid);

    const presenceRef = ref(database, `teams/${teamId}/presence/${currentUser.uid}`);
    
    // Function to update presence with current position
    const updatePresence = (x: number = 0, y: number = 0) => {
      const presenceData = {
        uid: currentUser.uid,
        displayName: currentUser.displayName || currentUser.email || 'User',
        photoURL: currentUser.photoURL || '',
        board: boardTag,
        x,
        y,
        lastActive: Date.now(),
      };
      console.log('Updating presence:', presenceData);
      set(presenceRef, presenceData);
    };

    // Initial presence update
    updatePresence();

    // Set up heartbeat every 30 seconds
    const heartbeatInterval = setInterval(() => {
      updatePresence(cursorPosition.x, cursorPosition.y);
    }, 30000);

    // Mouse move tracking for real-time cursors
    const handleMouseMove = (() => {
      let lastUpdate = 0;
      return (e: MouseEvent) => {
        const now = Date.now();
        if (now - lastUpdate < 100) return; // Throttle to 10fps for better performance

        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          const x = (e.clientX - rect.left) / (zoom / 100);
          const y = (e.clientY - rect.top) / (zoom / 100);
          
          setCursorPosition({ x, y });
          updatePresence(x, y);
          lastUpdate = now;
        }
      };
    })();

    // Remove presence on disconnect
    onDisconnect(presenceRef).remove();

    // Clean up presence on page unload
    const handleBeforeUnload = () => {
      set(presenceRef, null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Listen for all presence data
    const presenceListRef = ref(database, `teams/${teamId}/presence`);
    const unsubPresence = onValue(presenceListRef, (snapshot) => {
      const presenceData = snapshot.val();
      console.log('Presence data received:', presenceData);
      
      if (presenceData && team?.members) {
        const currentTime = Date.now();
        const activeThreshold = 2 * 60 * 1000; // 2 minutes

        const collaborators: OnlineCollaborator[] = [];
        
        Object.entries(presenceData).forEach(([uid, presence]: [string, any]) => {
          // Skip current user and check if user is team member
          if (uid === currentUser.uid || !team.members[uid]) {
            console.log('Skipping user:', uid, 'Current user:', uid === currentUser.uid, 'Is team member:', !!team.members[uid]);
            return;
          }
          
          if (presence && 
              presence.lastActive && 
              presence.board === boardTag &&
              (currentTime - presence.lastActive) < activeThreshold) {
            
            const member = team.members[uid];
            const colorIndex = Object.keys(team.members).indexOf(uid) % collaboratorColors.length;
            
            const collaborator = {
              id: uid,
              name: presence.displayName || member.email,
              avatar: presence.photoURL || '',
              email: member.email,
              role: member.role,
              color: collaboratorColors[colorIndex],
              cursor: presence.x !== undefined && presence.y !== undefined ? 
                { x: presence.x, y: presence.y } : undefined,
              lastActive: presence.lastActive
            };
            
            console.log('Adding collaborator:', collaborator);
            collaborators.push(collaborator);
          } else if (presence && (currentTime - presence.lastActive) >= activeThreshold) {
            // Clean up stale presence
            console.log('Cleaning up stale presence for:', uid);
            const staleRef = ref(database, `teams/${teamId}/presence/${uid}`);
            set(staleRef, null);
          }
        });

        console.log('Final collaborators list:', collaborators);
        setOnlineCollaborators(collaborators);
      } else {
        setOnlineCollaborators([]);
      }
    });

    return () => {
      clearInterval(heartbeatInterval);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      set(presenceRef, null);
      unsubPresence();
    };
  }, [teamId, currentUser, zoom]);

  // History Management
  const saveToHistory = useCallback((newElements: BoardElement[]) => {
    const newState: HistoryState = {
      elements: JSON.parse(JSON.stringify(newElements)),
      timestamp: Date.now()
    };
    
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(newState);
      // Keep only last 50 states
      return newHistory.slice(-50);
    });
    setHistoryIndex(prev => prev + 1);
  }, [historyIndex]);

  // Sync properties when selecting element
  useEffect(() => {
    if (selectedId) {
      const selectedElement = elements.find(el => el.id === selectedId);
      if (selectedElement) {
        setColor(selectedElement.color || '#3b82f6');
        setStrokeWidth(selectedElement.strokeWidth || 4);
        setOpacity(selectedElement.opacity || 1);
      }
    }
  }, [selectedId, elements]);

  // Apply property changes immediately to selected element (after sync effect)
  useEffect(() => {
    if (!selectedId) return;

    // Skip the first run right after the selection changes to avoid overwriting original element styles
    if (prevSelectedIdRef.current !== selectedId) {
      prevSelectedIdRef.current = selectedId;
      return;
    }

    const selectedElement = elements.find(el => el.id === selectedId);
    if (selectedElement) {
      const needsColor = selectedElement.color !== color;
      const needsStroke = selectedElement.type !== 'text' && selectedElement.strokeWidth !== strokeWidth;
      const needsOpacity = selectedElement.opacity !== opacity;
      if (needsColor || needsStroke || needsOpacity) {
        const updates: Partial<BoardElement> = { color, opacity };
        if (selectedElement.type !== 'text') {
          updates.strokeWidth = strokeWidth;
        }
        handleUpdateElement(selectedId, updates);
      }
    }
  }, [color, strokeWidth, opacity, selectedId, elements]);

  // Responsive handler
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(newIsMobile);
      if (newIsMobile && sidebarOpen && !sidebarCompact) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen, sidebarCompact]);

  // Real-time Firestore sync
  useEffect(() => {
    if (!boardId) return;
    const colRef = collection(firestore, elementsPath);
    const unsub = onSnapshot(colRef, snap => {
      const newElements = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as BoardElement));
      setElements(newElements);
      setLastModified(Date.now()); // Update last modified time
      
      // Save to history only if element count actually changed (using ref to avoid stale closure)
      if (newElements.length !== prevElementsLenRef.current) {
        prevElementsLenRef.current = newElements.length;
        saveToHistory(newElements);
      }
    });
    return () => unsub();
  }, [boardId, elementsPath]);

  // Auto-save functionality
  useEffect(() => {
    const autoSave = setTimeout(() => {
      if (saveStatus === 'saving') {
        setSaveStatus('saved');
      }
    }, 2000);
    return () => clearTimeout(autoSave);
  }, [saveStatus]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) {
        switch (e.key) {
          case 's':
            e.preventDefault();
            handleSave();
            break;
          case 'd':
            e.preventDefault();
            handleDuplicate();
            break;
        }
      }
      if (e.key === 'Delete' && selectedId) {
        handleDelete();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId]);

  // Transformer attachment
  useEffect(() => {
    if (selectedId && transformerRef.current && stageRef.current) {
      const stage = stageRef.current.getStage();
      const selectedNode = stage.findOne(`#el-${selectedId}`);
      if (selectedNode) {
        transformerRef.current.nodes([selectedNode]);
      }
    } else if (transformerRef.current) {
      transformerRef.current.nodes([]);
    }
  }, [selectedId]);

  // Generic update element function
  const handleUpdateElement = async (elementId: string, updates: Partial<BoardElement>) => {
    try {
      setSaveStatus('saving');
      const elRef = doc(firestore, elementsPath, elementId);
      await updateDoc(elRef, { ...updates, timestamp: Date.now() });
      setLastModified(Date.now());
      setSaveStatus('saved');
    } catch (error) {
      console.error('Error updating element:', error);
      setSaveStatus('error');
    }
  };

  // Tool handlers
  const handleAddImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSaveStatus('saving');
      
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image too large! Please choose an image smaller than 5MB.');
        setSaveStatus('saved');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = async (event) => {
        if (event.target?.result) {
          try {
            // Compress image if needed
            const compressedDataUrl = await compressImage(event.target.result as string, 0.8);
            
            const maxZIndex = Math.max(...elements.map(el => el.zIndex || 0), 0);
            const el: Omit<BoardElement, 'id'> = {
              type: 'image',
              content: compressedDataUrl,
              x: Math.random() * 300 + 100,
              y: Math.random() * 300 + 100,
              width: 300,
              height: 200,
              layer: 0,
              opacity: opacity,
              visible: true,
              locked: false,
              zIndex: maxZIndex + 1,
              timestamp: Date.now()
            };
            const colRef = collection(firestore, elementsPath);
            await addDoc(colRef, el);
            setLastModified(Date.now());
            setSaveStatus('saved');
          } catch (error) {
            console.error('Error adding image:', error);
            alert('Failed to add image. Please try a smaller image.');
            setSaveStatus('error');
          }
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Image compression function
  const compressImage = (dataUrl: string, quality: number = 0.8): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Calculate new dimensions (max 1200px width/height)
        const maxSize = 1200;
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        } else {
          resolve(dataUrl);
        }
      };
      img.src = dataUrl;
    });
  };

  const handleAddText = async () => {
    if (!newText.trim()) return;
    setSaveStatus('saving');
    const maxZIndex = Math.max(...elements.map(el => el.zIndex || 0), 0);
    const el: Omit<BoardElement, 'id'> = {
      type: 'text',
      content: newText,
      x: Math.random() * 200 + 120,
      y: Math.random() * 200 + 120,
      width: 200,
      height: 60,
      color: color,
      fontSize: 28,
      fontFamily: 'Inter',
      layer: 0,
      opacity: opacity,
      visible: true,
      locked: false,
      zIndex: maxZIndex + 1,
      timestamp: Date.now()
    };
    const colRef = collection(firestore, elementsPath);
    await addDoc(colRef, el);
    setTextDialog(false);
    setNewText('');
    setLastModified(Date.now());
    setSaveStatus('saved');
  };

  const handleAddShape = async (shapeType: 'rect' | 'circle' | 'line' | 'arrow') => {
    setSaveStatus('saving');
    const maxZIndex = Math.max(...elements.map(el => el.zIndex || 0), 0);
    
    const baseProps = {
      layer: 0,
      opacity: opacity,
      visible: true,
      locked: false,
      color: color,
      strokeWidth: strokeWidth,
      zIndex: maxZIndex + 1,
      timestamp: Date.now()
    };

    let el: Omit<BoardElement, 'id'>;
    switch (shapeType) {
      case 'rect':
        el = {
          type: 'rect',
          content: '',
          x: Math.random() * 200 + 150,
          y: Math.random() * 200 + 150,
          width: 120,
          height: 80,
          ...baseProps
        };
        break;
      case 'circle':
        el = {
          type: 'circle',
          content: '',
          x: Math.random() * 200 + 180,
          y: Math.random() * 200 + 180,
          width: 80,
          height: 80,
          ...baseProps
        };
        break;
      case 'line':
        el = {
          type: 'line',
          content: '',
          x: Math.random() * 200 + 200,
          y: Math.random() * 200 + 200,
          width: 120,
          height: 0,
          ...baseProps
        };
        break;
      case 'arrow':
        el = {
          type: 'arrow',
          content: '',
          x: Math.random() * 200 + 220,
          y: Math.random() * 200 + 220,
          width: 120,
          height: 0,
          ...baseProps
        };
        break;
    }
    
    const colRef = collection(firestore, elementsPath);
    await addDoc(colRef, el);
    setLastModified(Date.now());
    setSaveStatus('saved');
  };

  const handleAddColorSwatch = async () => {
    setSaveStatus('saving');
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F'];
    const randomColor = colors[Math.floor(Math.random() * colors.length)];
    const maxZIndex = Math.max(...elements.map(el => el.zIndex || 0), 0);
    const el: Omit<BoardElement, 'id'> = {
      type: 'colorSwatch',
      content: randomColor,
      x: Math.random() * 200 + 120,
      y: Math.random() * 200 + 120,
      width: 80,
      height: 80,
      color: randomColor,
      layer: 0,
      opacity: opacity,
      visible: true,
      locked: false,
      zIndex: maxZIndex + 1,
      timestamp: Date.now()
    };
    const colRef = collection(firestore, elementsPath);
    await addDoc(colRef, el);
    setLastModified(Date.now());
    setSaveStatus('saved');
  };

  // Layer ordering functions
  const bringToFront = async (elementId: string) => {
    const maxZIndex = Math.max(...elements.map(el => el.zIndex || 0), 0);
    await handleUpdateElement(elementId, { zIndex: maxZIndex + 1 });
  };

  const sendToBack = async (elementId: string) => {
    const minZIndex = Math.min(...elements.map(el => el.zIndex || 0), 0);
    await handleUpdateElement(elementId, { zIndex: minZIndex - 1 });
  };

  const moveUp = async (elementId: string) => {
    const element = elements.find(el => el.id === elementId);
    if (element) {
      const currentZ = element.zIndex || 0;
      await handleUpdateElement(elementId, { zIndex: currentZ + 1 });
    }
  };

  const moveDown = async (elementId: string) => {
    const element = elements.find(el => el.id === elementId);
    if (element) {
      const currentZ = element.zIndex || 0;
      await handleUpdateElement(elementId, { zIndex: currentZ - 1 });
    }
  };

  // Canvas interaction handlers
  const handleSelect = (id: string) => {
    const element = elements.find(el => el.id === id);
    if (element && !element.locked) {
      setSelectedId(id);
    }
  };

  const handleDeselect = () => setSelectedId(null);

  const handleDrag = async (id: string, x: number, y: number) => {
    try {
      setSaveStatus('saving');
      const newX = snapToGrid ? Math.round(x / 20) * 20 : x;
      const newY = snapToGrid ? Math.round(y / 20) * 20 : y;
      await handleUpdateElement(id, { x: newX, y: newY });
      setSaveStatus('saved');
    } catch (error) {
      console.error('Error updating element position:', error);
      setSaveStatus('error');
    }
  };

  const handleTransform = async (id: string, node: any) => {
    try {
      setSaveStatus('saving');
      await handleUpdateElement(id, {
        x: node.x(),
        y: node.y(),
        width: node.width() * node.scaleX(),
        height: node.height() * node.scaleY(),
        rotation: node.rotation(),
      });
      node.scaleX(1);
      node.scaleY(1);
      setSaveStatus('saved');
    } catch (error) {
      console.error('Error transforming element:', error);
      setSaveStatus('error');
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    try {
      setSaveStatus('saving');
      const elRef = doc(firestore, elementsPath, selectedId);
      await deleteDoc(elRef);
      setSelectedId(null);
      setLastModified(Date.now());
      setSaveStatus('saved');
    } catch (error) {
      console.error('Error deleting element:', error);
      setSaveStatus('error');
    }
  };

  const handleDuplicate = async () => {
    if (!selectedId) return;
    const elementToDuplicate = elements.find(el => el.id === selectedId);
    if (!elementToDuplicate) return;
    
    try {
      setSaveStatus('saving');
      const maxZIndex = Math.max(...elements.map(el => el.zIndex || 0), 0);
      const newElement: Omit<BoardElement, 'id'> = {
        ...elementToDuplicate,
        x: elementToDuplicate.x + 20,
        y: elementToDuplicate.y + 20,
        zIndex: maxZIndex + 1,
        timestamp: Date.now()
      };
      
      const colRef = collection(firestore, elementsPath);
      await addDoc(colRef, newElement);
      setSelectedId(null);
      setLastModified(Date.now());
      setSaveStatus('saved');
    } catch (error) {
      console.error('Error duplicating element:', error);
      setSaveStatus('error');
    }
  };

  // Unsplash integration
  const handleUnsplashSearch = async () => {
    setUnsplashLoading(true);
    setUnsplashResults([]);
    try {
      const res = await fetch(`https://api.unsplash.com/search/photos?query=${encodeURIComponent(unsplashQuery)}&per_page=12&client_id=${UNSPLASH_ACCESS_KEY}`);
      const data = await res.json();
      setUnsplashResults(data.results || []);
    } catch (err) {
      setUnsplashResults([]);
    }
    setUnsplashLoading(false);
  };

  const handleAddUnsplashImage = async (url: string) => {
    setSaveStatus('saving');
    const maxZIndex = Math.max(...elements.map(el => el.zIndex || 0), 0);
    const el: Omit<BoardElement, 'id'> = {
      type: 'image',
      content: url,
      x: Math.random() * 200 + 100,
      y: Math.random() * 200 + 100,
      width: 300,
      height: 200,
      layer: 0,
      opacity: opacity,
      visible: true,
      locked: false,
      zIndex: maxZIndex + 1,
      timestamp: Date.now()
    };
    const colRef = collection(firestore, elementsPath);
    await addDoc(colRef, el);
    setUnsplashDialog(false);
    setUnsplashQuery('');
    setUnsplashResults([]);
    setLastModified(Date.now());
    setSaveStatus('saved');
  };

  // AI Generate functionality
  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    
    try {
      // Try backend AI first
      const lower = aiPrompt.toLowerCase();

      let backendResult: { type: 'image' | 'text'; content: string } | null = null;

      try {
        if (lower.includes('image') || lower.includes('logo') || lower.includes('photo') || lower.includes('picture')) {
          const genImg = httpsCallable(fbFunctions, 'generateAiImage');
          const res: any = await genImg({ prompt: aiPrompt });
          backendResult = { type: 'image', content: res.data.url };
        } else {
          const genTxt = httpsCallable(fbFunctions, 'generateAiText');
          const res: any = await genTxt({ prompt: aiPrompt, maxChars: 40 });
          backendResult = { type: 'text', content: res.data.text };
        }
      } catch (err) {
        console.warn('Backend AI failed, falling back to local heuristic', err);
      }

      // If we got backend result, create element directly
      if (backendResult) {
        const maxZIndex = Math.max(...elements.map(el => el.zIndex || 0), 0);
        let el: Omit<BoardElement, 'id'>;
        if (backendResult.type === 'image') {
          el = {
            type: 'image',
            content: backendResult.content,
            x: Math.random() * 200 + 150,
            y: Math.random() * 200 + 150,
            width: 300,
            height: 300,
            layer: 0,
            opacity: 1,
            visible: true,
            locked: false,
            zIndex: maxZIndex + 1,
            timestamp: Date.now(),
          };
        } else {
          el = {
            type: 'text',
            content: backendResult.content,
            x: Math.random() * 200 + 150,
            y: Math.random() * 200 + 150,
            width: 250,
            height: 60,
            color: '#0ea5e9',
            fontSize: 28,
            fontFamily: 'Inter',
            layer: 0,
            opacity: 1,
            visible: true,
            locked: false,
            zIndex: maxZIndex + 1,
            timestamp: Date.now(),
          };
        }

        await addDoc(collection(firestore, elementsPath), el);
        setAiDialog(false);
        setAiPrompt('');
        setAiLoading(false);
        return;
      }

      // --- existing heuristic fallback ---
      // Simulate AI generation with more varied content
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Generate different types of content based on prompt keywords
      const prompt = lower;

      const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#FF9F43', '#6C5CE7'];
      const maxZIndex = Math.max(...elements.map(el => el.zIndex || 0), 0);
      
      // Generate different types of content based on prompt keywords
      let generatedElement: Omit<BoardElement, 'id'>;
      
      // Nature-inspired prompts should generate images or nature-themed text
      if (prompt.includes('nature') || prompt.includes('background') || prompt.includes('landscape') || 
          prompt.includes('forest') || prompt.includes('ocean') || prompt.includes('mountain') ||
          prompt.includes('sunset') || prompt.includes('garden') || prompt.includes('flower')) {
        
        // For nature prompts, prefer generating images or nature-themed text
        const natureTexts = [
          'Nature\'s Beauty',
          'Organic Design',
          'Natural Harmony',
          'Eco-Friendly',
          'Sustainable Living',
          'Green Inspiration',
          'Earth Tones',
          'Natural Elements'
        ];
        
        // 70% chance for text, 30% chance for image placeholder
        if (Math.random() < 0.7) {
          generatedElement = {
            type: 'text',
            content: natureTexts[Math.floor(Math.random() * natureTexts.length)],
            x: Math.random() * 200 + 150,
            y: Math.random() * 200 + 150,
            width: 250,
            height: 60,
            color: '#4ECDC4', // Nature green
            fontSize: 28,
            fontFamily: 'Inter',
            layer: 0,
            opacity: 1,
            visible: true,
            locked: false,
            zIndex: maxZIndex + 1,
            timestamp: Date.now()
          };
        } else {
          // Generate a placeholder for nature image
          generatedElement = {
            type: 'image',
            content: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop', // Nature placeholder
            x: Math.random() * 200 + 150,
            y: Math.random() * 200 + 150,
            width: 300,
            height: 200,
            layer: 0,
            opacity: 1,
            visible: true,
            locked: false,
            zIndex: maxZIndex + 1,
            timestamp: Date.now()
          };
        }
      } else if (prompt.includes('text') || prompt.includes('quote') || prompt.includes('title') || 
                 prompt.includes('heading') || prompt.includes('label')) {
        // Generate text element
        const sampleTexts = [
          'AI Generated Text',
          'Creative Ideas',
          'Innovation Hub', 
          'Design Thinking',
          'Brainstorm Session',
          'Inspiration Board',
          'Creative Process',
          'Design Strategy'
        ];
        generatedElement = {
          type: 'text',
          content: sampleTexts[Math.floor(Math.random() * sampleTexts.length)],
          x: Math.random() * 200 + 150,
          y: Math.random() * 200 + 150,
          width: 200,
          height: 50,
          color: colors[Math.floor(Math.random() * colors.length)],
          fontSize: 24,
          fontFamily: 'Inter',
          layer: 0,
          opacity: 1,
          visible: true,
          locked: false,
          zIndex: maxZIndex + 1,
          timestamp: Date.now()
        };
      } else if (prompt.includes('image') || prompt.includes('photo') || prompt.includes('picture') ||
                 prompt.includes('visual') || prompt.includes('graphic')) {
        // Try fetching a relevant Unsplash image using the prompt
        let imageUrl = '';
        try {
          const res = await fetch(
            `https://api.unsplash.com/search/photos?query=${encodeURIComponent(aiPrompt)}&per_page=1&orientation=landscape&client_id=${UNSPLASH_ACCESS_KEY}`
          );
          const data = await res.json();
          if (data.results && data.results.length > 0) {
            imageUrl = data.results[0].urls.small;
          }
        } catch (e) {
          console.error('Unsplash fetch error:', e);
        }

        // Fallback placeholder if Unsplash fails/no result
        if (!imageUrl) {
          imageUrl = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop';
        }

        generatedElement = {
          type: 'image',
          content: imageUrl,
          x: Math.random() * 200 + 150,
          y: Math.random() * 200 + 150,
          width: 300,
          height: 200,
          layer: 0,
          opacity: 1,
          visible: true,
          locked: false,
          zIndex: maxZIndex + 1,
          timestamp: Date.now()
        };
      } else if (prompt.includes('circle') || prompt.includes('round')) {
        // Generate circle
        generatedElement = {
          type: 'circle',
          content: '',
          x: Math.random() * 200 + 150,
          y: Math.random() * 200 + 150,
          width: 100,
          height: 100,
          color: colors[Math.floor(Math.random() * colors.length)],
          layer: 0,
          opacity: 0.8,
          visible: true,
          locked: false,
          zIndex: maxZIndex + 1,
          timestamp: Date.now()
        };
      } else if (prompt.includes('color') || prompt.includes('palette')) {
        // Generate color swatch
        generatedElement = {
          type: 'colorSwatch',
          content: colors[Math.floor(Math.random() * colors.length)],
          x: Math.random() * 200 + 150,
          y: Math.random() * 200 + 150,
          width: 80,
          height: 80,
          color: colors[Math.floor(Math.random() * colors.length)],
          layer: 0,
          opacity: 1,
          visible: true,
          locked: false,
          zIndex: maxZIndex + 1,
          timestamp: Date.now()
        };
      } else {
        // Default: Generate text for most prompts (more useful than random shapes)
        const defaultTexts = [
          'Creative Element',
          'Design Component',
          'Visual Asset',
          'Content Block',
          'Design Element',
          'Creative Block'
        ];
        generatedElement = {
          type: 'text',
          content: defaultTexts[Math.floor(Math.random() * defaultTexts.length)],
          x: Math.random() * 200 + 150,
          y: Math.random() * 200 + 150,
          width: 200,
          height: 50,
          color: colors[Math.floor(Math.random() * colors.length)],
          fontSize: 24,
          fontFamily: 'Inter',
          layer: 0,
          opacity: 1,
          visible: true,
          locked: false,
          zIndex: maxZIndex + 1,
          timestamp: Date.now()
        };
      }
      
      setSaveStatus('saving');
      const colRef = collection(firestore, elementsPath);
      await addDoc(colRef, generatedElement);
      
      setAiDialog(false);
      setAiPrompt('');
      setLastModified(Date.now());
      setSaveStatus('saved');
    } catch (error) {
      console.error('AI generation error:', error);
      setSaveStatus('error');
      alert('Failed to generate AI content. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  // Utility functions
  const handleSave = () => {
    setSaveStatus('saving');
    setLastModified(Date.now());
    setTimeout(() => setSaveStatus('saved'), 1000);
  };



  const handleZoomIn = () => setZoom(prev => Math.min(300, prev + 25));
  const handleZoomOut = () => setZoom(prev => Math.max(25, prev - 25));
  const handleZoomReset = () => setZoom(100);

  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setFullscreen(true);
        setMoreMenuAnchor(null);
      }).catch(err => {
        console.error('Error attempting to enable fullscreen:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setFullscreen(false);
        setMoreMenuAnchor(null);
      }).catch(err => {
        console.error('Error attempting to exit fullscreen:', err);
      });
    }
  };

  const handleExport = async () => {
    if (!stageRef.current) return;
    
    try {
      const stage = stageRef.current.getStage();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) throw new Error('Could not get canvas context');
      
      canvas.width = BOARD_MIN_WIDTH * 2;
      canvas.height = BOARD_MIN_HEIGHT * 2;
      
      ctx.fillStyle = canvasBackground;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const stageDataURL = stage.toDataURL({
        mimeType: 'image/png',
        quality: 1,
        pixelRatio: 2,
      });
      
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        const finalDataURL = canvas.toDataURL('image/png', 1);
        const link = document.createElement('a');
        link.download = `${teamName || 'moodboard'}-${new Date().toISOString().split('T')[0]}.png`;
        link.href = finalDataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };
      
      img.src = stageDataURL;
      
    } catch (error) {
      console.error('Error exporting mood board:', error);
      alert('Failed to export mood board. Please try again.');
    }
  };

  // Layer management
  const toggleLayerVisibility = async (layerId: string) => {
    setLayers(prev => prev.map(layer => 
      layer.id === layerId ? { ...layer, visible: !layer.visible } : layer
    ));
    
    const layerIndex = layers.findIndex(l => l.id === layerId);
    const layerElements = elements.filter(el => el.layer === layerIndex);
    
    for (const element of layerElements) {
      await handleUpdateElement(element.id, { visible: !element.visible });
    }
  };

  const toggleLayerLock = async (layerId: string) => {
    setLayers(prev => prev.map(layer => 
      layer.id === layerId ? { ...layer, locked: !layer.locked } : layer
    ));
    
    const layerIndex = layers.findIndex(l => l.id === layerId);
    const layerElements = elements.filter(el => el.layer === layerIndex);
    
    for (const element of layerElements) {
      await handleUpdateElement(element.id, { locked: !element.locked });
    }
  };

  // Calculate dynamic dimensions
  const canvasWidth = Math.max(BOARD_MIN_WIDTH, window.innerWidth - (sidebarOpen && !isMobile ? (sidebarCompact ? COMPACT_SIDEBAR_WIDTH : SIDEBAR_WIDTH) : 0) - 40);
  const canvasHeight = Math.max(BOARD_MIN_HEIGHT, window.innerHeight - 160);

  // Professional Header Component
  const ProfessionalHeader = () => (
    <Paper 
      elevation={0} 
      sx={{ 
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: '#ffffff',
        position: 'sticky',
        top: 0,
        zIndex: 1200
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        px: 3,
        py: 1.5,
        minHeight: 64
      }}>
        {/* Left Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isMobile && (
            <IconButton onClick={() => navigate(-1)} size="small">
              <ArrowDropDown sx={{ transform: 'rotate(90deg)' }} />
            </IconButton>
          )}
          
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {teamName} Mood Board
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Chip 
                label={saveStatus === 'saved' ? 'Saved' : saveStatus === 'saving' ? 'Saving...' : 'Error'} 
                size="small"
                color={saveStatus === 'saved' ? 'success' : saveStatus === 'saving' ? 'warning' : 'error'}
                variant="outlined"
              />
              <Typography variant="caption" color="text.secondary">
                Last edited {formatTimeAgo(lastModified)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Center - Zoom Controls (Desktop) */}
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={handleZoomOut} size="small">
              <ZoomOut />
            </IconButton>
            <Chip label={`${zoom}%`} size="small" onClick={handleZoomReset} clickable />
            <IconButton onClick={handleZoomIn} size="small">
              <ZoomIn />
            </IconButton>
          </Box>
        )}

        {/* Right Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Real Team Members Online */}
          {onlineCollaborators.length > 0 && (
            <AvatarGroup max={3} sx={{ mr: 1 }}>
              {onlineCollaborators.map(collaborator => (
                <Tooltip key={collaborator.id} title={`${collaborator.name} (${collaborator.role}) - Online`}>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        bgcolor: 'success.main', 
                        borderRadius: '50%',
                        border: '1px solid white'
                      }} />
                    }
                  >
                    <Avatar 
                      src={collaborator.avatar}
                      sx={{ 
                        width: 32, 
                        height: 32, 
                        fontSize: '0.75rem',
                        bgcolor: collaborator.color,
                        border: `2px solid ${collaborator.color}`
                      }}
                    >
                      {collaborator.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </Avatar>
                  </Badge>
                </Tooltip>
              ))}
            </AvatarGroup>
          )}

          {/* Action Buttons */}
          {!isMobile && (
            <Button
              variant="contained"
              startIcon={<Save />}
              size="small"
              onClick={handleSave}
              sx={{ mr: 1 }}
            >
              Save
            </Button>
          )}

          <IconButton onClick={(e) => setMoreMenuAnchor(e.currentTarget)} size="small">
            <MoreVert />
          </IconButton>

          {/* Mobile Menu */}
          {isMobile && (
            <IconButton onClick={() => setSidebarOpen(!sidebarOpen)} size="small">
              <MenuIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Mobile Quick Actions */}
      {isMobile && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-around', 
          alignItems: 'center',
          py: 1,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton onClick={handleZoomOut} size="small">
              <ZoomOut />
            </IconButton>
            <Typography variant="caption">{zoom}%</Typography>
            <IconButton onClick={handleZoomIn} size="small">
              <ZoomIn />
            </IconButton>
          </Box>
          <Button variant="contained" size="small" onClick={handleSave}>
            Save
          </Button>
        </Box>
      )}
    </Paper>
  );

  // Professional Sidebar Component
  const ProfessionalSidebar = () => (
    <Drawer
      variant={isMobile ? "temporary" : "persistent"}
      anchor="left"
      open={sidebarOpen}
      onClose={() => setSidebarOpen(false)}
      PaperProps={{
        sx: {
          width: sidebarCompact ? COMPACT_SIDEBAR_WIDTH : SIDEBAR_WIDTH,
          bgcolor: '#f8fafc',
          borderRight: '1px solid',
          borderColor: 'divider',
          transition: 'width 0.3s ease'
        }
      }}
    >
      {/* Sidebar Header */}
      <Box sx={{ 
        p: 2, 
        borderBottom: '1px solid', 
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        {!sidebarCompact && (
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Tools
          </Typography>
        )}
        <IconButton 
          onClick={() => setSidebarCompact(!sidebarCompact)}
          size="small"
        >
          {sidebarCompact ? <MenuIcon /> : <Close />}
        </IconButton>
      </Box>

      {/* Tabs */}
      {!sidebarCompact && (
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Tab label="Tools" />
          <Tab label="Layers" />
          <Tab label="Assets" />
        </Tabs>
      )}

      {/* Tab Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: sidebarCompact ? 1 : 2 }}>
        {(activeTab === 0 || sidebarCompact) && (
          <ToolsPanel compact={sidebarCompact} />
        )}
        {activeTab === 1 && !sidebarCompact && (
          <LayersPanel />
        )}
        {activeTab === 2 && !sidebarCompact && (
          <AssetsPanel />
        )}
      </Box>
    </Drawer>
  );

  // Tools Panel Component
  const ToolsPanel = ({ compact }: { compact: boolean }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: compact ? 1 : 2 }}>
      {/* Basic Tools */}
      <Box>
        {!compact && (
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Basic Tools
          </Typography>
        )}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: compact ? '1fr' : 'repeat(2, 1fr)', 
          gap: 1 
        }}>
          <Tooltip title="Upload Image" placement="right">
            <IconButton 
              component="label"
              color="inherit"
            >
              <AddPhotoAlternate />
              <input 
                type="file" 
                hidden 
                accept="image/*" 
                onChange={handleAddImage} 
              />
            </IconButton>
          </Tooltip>

          <Tooltip title="Add Text" placement="right">
            <IconButton 
              onClick={() => setTextDialog(true)}
              color="inherit"
            >
              <TextFields />
            </IconButton>
          </Tooltip>

          <Tooltip title="Rectangle" placement="right">
            <IconButton 
              onClick={() => handleAddShape('rect')}
              color="inherit"
            >
              <CropSquare />
            </IconButton>
          </Tooltip>

          <Tooltip title="Circle" placement="right">
            <IconButton 
              onClick={() => handleAddShape('circle')}
              color="inherit"
            >
              <RadioButtonUnchecked />
            </IconButton>
          </Tooltip>

          <Tooltip title="Color Swatch" placement="right">
            <IconButton 
              onClick={handleAddColorSwatch}
              color="inherit"
            >
              <Palette />
            </IconButton>
          </Tooltip>

          <Tooltip title="Search Unsplash" placement="right">
            <IconButton 
              onClick={() => setUnsplashDialog(true)}
              color="inherit"
            >
              <Search />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Properties */}
      {!compact && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Properties
          </Typography>
          
          {/* Color Picker */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Color
            </Typography>
            <Box 
              sx={{ 
                width: '100%', 
                height: 40, 
                bgcolor: color, 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                mt: 0.5
              }}
              onClick={(e) => setColorPickerAnchor(e.currentTarget)}
            />
          </Box>

          {/* Stroke Width */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Stroke Width: {strokeWidth}px
            </Typography>
            <Slider
              value={strokeWidth}
              onChange={(_, value) => setStrokeWidth(value as number)}
              min={1}
              max={20}
              size="small"
              sx={{ mt: 0.5 }}
            />
          </Box>

          {/* Opacity */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Opacity: {Math.round(opacity * 100)}%
            </Typography>
            <Slider
              value={opacity}
              onChange={(_, value) => setOpacity(value as number)}
              min={0}
              max={1}
              step={0.1}
              size="small"
              sx={{ mt: 0.5 }}
            />
          </Box>
        </Box>
      )}

      {/* Canvas Controls */}
      {!compact && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Canvas
          </Typography>
          
          <FormControlLabel
            control={
              <Switch 
                checked={showGrid} 
                onChange={(e) => setShowGrid(e.target.checked)}
                size="small"
              />
            }
            label="Show Grid"
            sx={{ mb: 1 }}
          />
          
          <FormControlLabel
            control={
              <Switch 
                checked={snapToGrid} 
                onChange={(e) => setSnapToGrid(e.target.checked)}
                size="small"
              />
            }
            label="Snap to Grid"
            sx={{ mb: 1 }}
          />
        </Box>
      )}
    </Box>
  );

  // Enhanced Layers Panel Component (removed arrow controls as requested)
  const LayersPanel = () => {
    const getElementsInLayer = (layerIndex: number) => {
      return elements.filter(el => el.layer === layerIndex);
    };

    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          Layers
        </Typography>
        
        {layers.map((layer, index) => {
          const layerElements = getElementsInLayer(index);
          return (
            <Paper
              key={layer.id}
              elevation={0}
              sx={{ 
                p: 1.5, 
                mb: 1, 
                border: '1px solid',
                borderColor: activeLayer === layer.id ? 'primary.main' : 'divider',
                bgcolor: activeLayer === layer.id ? 'action.selected' : 'background.paper',
                cursor: 'pointer'
              }}
              onClick={() => setActiveLayer(layer.id)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DragIndicator sx={{ color: 'text.disabled', fontSize: 16 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {layer.name}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <IconButton 
                    size="small" 
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleLayerVisibility(layer.id);
                    }}
                  >
                    {layer.visible ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                  <IconButton 
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleLayerLock(layer.id);
                    }}
                  >
                    {layer.locked ? <Lock /> : <LockOpen />}
                  </IconButton>
                </Box>
              </Box>
              
              <Typography variant="caption" color="text.secondary">
                {layerElements.length} elements
              </Typography>

              {/* Layer Elements with Arrow Controls */}
              {layerElements.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {layerElements
                    .sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0))
                    .map(element => (
                    <Box 
                      key={element.id}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        p: 0.5,
                        mb: 0.5,
                        bgcolor: selectedId === element.id ? 'primary.light' : 'action.hover',
                        borderRadius: 1,
                        cursor: 'pointer'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(element.id);
                      }}
                    >
                      <Typography variant="caption">
                        {element.type === 'text' ? element.content.slice(0, 15) + '...' : element.type}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Bring to Front">
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              bringToFront(element.id);
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <VerticalAlignTop fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Move Up">
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              moveUp(element.id);
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <KeyboardArrowUp fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Move Down">
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              moveDown(element.id);
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <KeyboardArrowDown fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Send to Back">
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              sendToBack(element.id);
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <VerticalAlignBottom fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          );
        })}

      </Box>
    );
  };

  // Updated Assets Panel Component (removed templates)
  const AssetsPanel = () => (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        Assets & AI
      </Typography>
      
      <Button
        variant="outlined"
        startIcon={<PhotoLibrary />}
        fullWidth
        sx={{ mb: 2 }}
        onClick={() => setUnsplashDialog(true)}
      >
        Browse Images
      </Button>
      
      <Button
        variant="contained"
        startIcon={<AutoAwesome />}
        fullWidth
        sx={{ mb: 1 }}
        onClick={() => setAiDialog(true)}
      >
        AI Generate
      </Button>
      
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Generate content using AI based on your prompts
      </Typography>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ 
        display: 'flex', 
        height: '100vh', 
        bgcolor: '#f5f5f5',
        overflow: 'hidden'
      }}>
        {/* Professional Header */}
        <Box sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          zIndex: 1300 
        }}>
          <ProfessionalHeader />
        </Box>

        {/* Professional Sidebar */}
        <ProfessionalSidebar />

        {/* Main Canvas Area */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          marginTop: isMobile ? '120px' : '80px',
          marginLeft: sidebarOpen && !isMobile ? (sidebarCompact ? `${COMPACT_SIDEBAR_WIDTH}px` : `${SIDEBAR_WIDTH}px`) : 0,
          transition: 'margin-left 0.3s ease'
        }}>
          {/* Canvas Container */}
          <Box 
            ref={containerRef}
            sx={{ 
              flex: 1, 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              p: 2,
              position: 'relative',
              overflow: 'auto'
            }}
          >
            {/* Canvas with Grid Background */}
            <Paper
              elevation={3}
              sx={{
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: canvasBackground,
                backgroundImage: showGrid ? 
                  'radial-gradient(circle, #ccc 1px, transparent 1px)' : 'none',
                backgroundSize: showGrid ? '20px 20px' : 'auto'
              }}
            >
              <Stage 
                ref={stageRef} 
                width={canvasWidth} 
                height={canvasHeight}
                scaleX={zoom / 100} 
                scaleY={zoom / 100}
                onClick={(e) => {
                  const clickedOnEmpty = e.target === e.target.getStage();
                  if (clickedOnEmpty) {
                    setSelectedId(null);
                  }
                }}
              >
                <Layer>
                  {elements
                    .filter(el => el.visible !== false)
                    .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
                    .map(el => {
                      const commonProps = {
                        key: el.id,
                        id: `el-${el.id}`,
                        x: el.x,
                        y: el.y,
                        draggable: !el.locked,
                        opacity: el.opacity || 1,
                        onClick: () => handleSelect(el.id),
                        onDragEnd: (e: any) => handleDrag(el.id, e.target.x(), e.target.y()),
                        onTransformEnd: (e: any) => handleTransform(el.id, e.target)
                      };

                      switch (el.type) {
                        case 'image':
                          const img = new window.Image();
                          img.src = el.content;
                          return (
                            <KonvaImage
                              {...commonProps}
                              image={img}
                              width={el.width}
                              height={el.height}
                            />
                          );
                        
                        case 'text':
                          return (
                            <KonvaText
                              {...commonProps}
                              text={el.content}
                              width={el.width}
                              height={el.height}
                              fontSize={el.fontSize || 28}
                              fill={el.color || '#222'}
                              fontFamily={el.fontFamily || 'Inter'}
                              fontStyle={`${el.bold ? 'bold' : ''} ${el.italic ? 'italic' : ''}`.trim()}
                              align={el.align || 'left'}
                              textDecoration={el.underline ? 'underline' : ''}
                            />
                          );
                        
                        case 'rect':
                          return (
                            <Rect
                              {...commonProps}
                              width={el.width}
                              height={el.height}
                              fill={el.color || '#3b82f6'}
                              stroke={el.strokeWidth ? '#000' : undefined}
                              strokeWidth={el.strokeWidth || 0}
                            />
                          );
                        
                        case 'circle':
                          return (
                            <Ellipse
                              {...commonProps}
                              radiusX={el.width / 2}
                              radiusY={el.height / 2}
                              fill={el.color || '#3b82f6'}
                              stroke={el.strokeWidth ? '#000' : undefined}
                              strokeWidth={el.strokeWidth || 0}
                            />
                          );
                        
                        case 'line':
                          return (
                            <Line
                              {...commonProps}
                              points={[0, 0, el.width, el.height]}
                              stroke={el.color || '#3b82f6'}
                              strokeWidth={el.strokeWidth || 4}
                              lineCap="round"
                            />
                          );
                        
                        case 'arrow':
                          return (
                            <Arrow
                              {...commonProps}
                              points={[0, 0, el.width, el.height]}
                              pointerLength={16}
                              pointerWidth={16}
                              fill={el.color || '#3b82f6'}
                              stroke={el.color || '#3b82f6'}
                              strokeWidth={el.strokeWidth || 4}
                            />
                          );
                        
                        case 'colorSwatch':
                          return (
                            <Rect
                              {...commonProps}
                              width={el.width}
                              height={el.height}
                              fill={el.color || '#3b82f6'}
                              stroke="#ffffff"
                              strokeWidth={3}
                              cornerRadius={8}
                              shadowColor="rgba(0, 0, 0, 0.2)"
                              shadowBlur={6}
                              shadowOffset={{ x: 2, y: 2 }}
                            />
                          );
                        
                        default:
                          return null;
                      }
                    })}
                  
                  {/* Real-Time Collaboration Cursors */}
                  {onlineCollaborators.length > 0 && onlineCollaborators.map(collaborator => 
                    collaborator.cursor && (
                      <React.Fragment key={`cursor-${collaborator.id}`}>
                        {/* Cursor Arrow */}
                        <Arrow
                          x={collaborator.cursor.x}
                          y={collaborator.cursor.y}
                          points={[0, 0, 0, 20, 5, 15, 12, 15]}
                          fill={collaborator.color}
                          stroke="white"
                          strokeWidth={1}
                          closed
                          listening={false}
                        />
                        {/* Name Label */}
                        <Rect
                          x={collaborator.cursor.x + 15}
                          y={collaborator.cursor.y - 5}
                          width={collaborator.name.split(' ')[0].length * 8 + 10}
                          height={20}
                          fill={collaborator.color}
                          cornerRadius={10}
                          listening={false}
                        />
                        <KonvaText
                          x={collaborator.cursor.x + 20}
                          y={collaborator.cursor.y}
                          text={collaborator.name.split(' ')[0]} // First name only
                          fontSize={12}
                          fill="white"
                          fontFamily="Inter"
                          listening={false}
                        />
                      </React.Fragment>
                    )
                  )}
                  
                  {/* Transformer */}
                  {selectedId && (
                    <Transformer
                      ref={transformerRef}
                      rotateEnabled
                      enabledAnchors={["top-left","top-right","bottom-left","bottom-right"]}
                    />
                  )}
                </Layer>
              </Stage>
            </Paper>
          </Box>

          {/* Mobile Floating Action Button */}
          {isMobile && (
            <Fab
              color="primary"
              sx={{ position: 'fixed', bottom: 16, right: 16 }}
              onClick={() => setSidebarOpen(true)}
            >
              <Add />
            </Fab>
          )}
        </Box>

        {/* Color Picker Popover */}
        <Popover
          open={Boolean(colorPickerAnchor)}
          anchorEl={colorPickerAnchor}
          onClose={() => setColorPickerAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <SketchPicker
            color={color}
            onChange={(color) => setColor(color.hex)}
          />
        </Popover>

        {/* More Menu */}
        <Menu
          anchorEl={moreMenuAnchor}
          open={Boolean(moreMenuAnchor)}
          onClose={() => setMoreMenuAnchor(null)}
        >
          <MenuItem onClick={handleExport}>
            <CloudDownload sx={{ mr: 1 }} />
            Export
          </MenuItem>
          <MenuItem onClick={handleFullscreen}>
            {fullscreen ? <FullscreenExit sx={{ mr: 1 }} /> : <Fullscreen sx={{ mr: 1 }} />}
            {fullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          </MenuItem>
        </Menu>

        {/* Add Text Dialog */}
        <Dialog open={textDialog} onClose={() => setTextDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Text</DialogTitle>
          <DialogContent>
            <TextField
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              fullWidth
              autoFocus
              multiline
              minRows={3}
              placeholder="Enter your text here..."
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTextDialog(false)}>Cancel</Button>
            <Button onClick={handleAddText} variant="contained">Add Text</Button>
          </DialogActions>
        </Dialog>

        {/* Unsplash Dialog */}
        <Dialog open={unsplashDialog} onClose={() => setUnsplashDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Search Unsplash Images
            <IconButton 
              onClick={() => setUnsplashDialog(false)} 
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                value={unsplashQuery}
                onChange={(e) => setUnsplashQuery(e.target.value)}
                placeholder="Search for images..."
                fullWidth
                onKeyDown={(e) => { if (e.key === 'Enter') handleUnsplashSearch(); }}
              />
              <Button 
                variant="contained" 
                onClick={handleUnsplashSearch} 
                disabled={unsplashLoading}
                sx={{ minWidth: 100 }}
              >
                {unsplashLoading ? 'Searching...' : 'Search'}
              </Button>
            </Box>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: 2,
              maxHeight: 400,
              overflow: 'auto'
            }}>
              {unsplashResults.map(img => (
                <Card 
                  key={img.id}
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'scale(1.02)' }
                  }}
                  onClick={() => handleAddUnsplashImage(img.urls.small)}
                >
                  <Box
                    component="img"
                    src={img.urls.small}
                    alt={img.alt_description}
                    sx={{
                      width: '100%',
                      height: 150,
                      objectFit: 'cover'
                    }}
                  />
                  <CardContent sx={{ p: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      by {img.user.name}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </DialogContent>
        </Dialog>

        {/* AI Generate Dialog */}
        <Dialog open={aiDialog} onClose={() => setAiDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            AI Generate
            <IconButton 
              onClick={() => setAiDialog(false)} 
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Describe what you want to create and AI will generate it for you.
            </Typography>
            <TextField
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              placeholder="e.g., Create a colorful geometric pattern, Generate a minimalist logo, Design a nature-inspired background..."
              sx={{ mt: 1 }}
            />
            {aiLoading && (
              <Box sx={{ mt: 2 }}>
                <Skeleton variant="rectangular" height={60} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  AI is generating your content...
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAiDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleAiGenerate} 
              variant="contained" 
              disabled={!aiPrompt.trim() || aiLoading}
              startIcon={<AutoAwesome />}
            >
              {aiLoading ? 'Generating...' : 'Generate'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default MoodBoardEditorV3; 